/**
 * Marketing Tools — public SEO meta + remarketing pixel helpers.
 * Used on company listing and event booking pages.
 */

const META_ATTR = "data-meetocto-seo";
const PIXEL_ATTR = "data-meetocto-pixel";

const setOrCreateMeta = (name, content) => {
  if (!content) {
    const existing = document.querySelector(`meta[name="${name}"][${META_ATTR}]`);
    if (existing) {
      existing.remove();
    }
    return;
  }

  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.setAttribute(META_ATTR, "1");
};

/**
 * Company / OctoLink page meta (base SEO as saved).
 */
export const buildCompanySeoMeta = (seo) => {
  if (!seo) {
    return null;
  }
  return {
    title: (seo.title || "").trim(),
    description: (seo.description || "").trim(),
    keywords: (seo.keywords || "").trim(),
  };
};

/**
 * Event booking page meta — inherit business SEO and append event name.
 * Title:       "{Business} - {Event}"
 * Description: "{Event} - {Business description}"
 * Keywords:    "{keywords}, {Event}"
 */
export const buildEventSeoMeta = (seo, eventName) => {
  if (!seo) {
    return null;
  }

  const name = (eventName || "").trim();
  const baseTitle = (seo.title || "").trim();
  const baseDescription = (seo.description || "").trim();
  const baseKeywords = (seo.keywords || "").trim();

  return {
    title: baseTitle
      ? name
        ? `${baseTitle} - ${name}`
        : baseTitle
      : name,
    description: baseDescription
      ? name
        ? `${name} - ${baseDescription}`
        : baseDescription
      : "",
    keywords: baseKeywords
      ? name
        ? `${baseKeywords}, ${name}`
        : baseKeywords
      : name,
  };
};

/**
 * Apply title / description / keywords to the document.
 */
export const applySeoMeta = (meta) => {
  if (!meta || typeof document === "undefined") {
    return;
  }

  if (meta.title) {
    document.title = meta.title;
  }

  setOrCreateMeta("description", meta.description || "");
  setOrCreateMeta("keywords", meta.keywords || "");
};

const alreadyInjected = (key) =>
  !!document.querySelector(`[${PIXEL_ATTR}="${key}"]`);

const markScript = (script, key) => {
  script.setAttribute(PIXEL_ATTR, key);
  return script;
};

const injectInlineScript = (key, code) => {
  if (alreadyInjected(key) || !code) {
    return;
  }
  const script = markScript(document.createElement("script"), key);
  script.type = "text/javascript";
  script.text = code;
  document.head.appendChild(script);
};

const injectExternalScript = (key, src, async = true) => {
  if (alreadyInjected(key) || !src) {
    return;
  }
  const script = markScript(document.createElement("script"), key);
  script.src = src;
  script.async = async;
  document.head.appendChild(script);
};

/**
 * Embed remarketing / analytics scripts when valid IDs are present.
 * Safe to call multiple times — scripts are injected once per key.
 */
export const injectMarketingPixels = (seo) => {
  if (!seo || typeof document === "undefined") {
    return;
  }

  const fbId = (seo.facebook_pixel_id || "").trim();
  if (fbId && /^\d{15,16}$/.test(fbId)) {
    injectInlineScript(
      "facebook-base",
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init','${fbId}');
fbq('track','PageView');`
    );
  }

  const twId = (seo.twitter_pixel_id || "").trim();
  if (twId && /^[A-Za-z0-9]{5,64}$/.test(twId)) {
    injectInlineScript(
      "twitter-base",
      `!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
twq('config','${twId}');`
    );
  }

  const liId = (seo.linkedin_pixel_id || "").trim();
  if (liId && /^\d{5,20}$/.test(liId)) {
    injectInlineScript(
      "linkedin-base",
      `_linkedin_partner_id = "${liId}";
window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
window._linkedin_data_partner_ids.push(_linkedin_partner_id);
(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};
window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];
var b=document.createElement("script");b.type="text/javascript";b.async=true;
b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";
s.parentNode.insertBefore(b,s);})(window.lintrk);`
    );
  }

  const pinId = (seo.pinterest_pixel_id || "").trim();
  if (pinId && /^[A-Za-z0-9]{5,64}$/.test(pinId)) {
    injectInlineScript(
      "pinterest-base",
      `!function(e){if(!window.pintrk){window.pintrk=function(){window.pintrk.queue.push(Array.prototype.slice.call(arguments))};
var n=window.pintrk;n.queue=[],n.version="3.0";
var t=document.createElement("script");t.async=!0;t.src=e;
var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");
pintrk('load','${pinId}');
pintrk('page');`
    );
  }

  const quoraId = (seo.quora_pixel_id || "").trim();
  if (quoraId && /^[A-Za-z0-9\-_]{5,64}$/.test(quoraId)) {
    injectInlineScript(
      "quora-base",
      `!function(q,e,v,n,t,s){if(q.qp)return;n=q.qp=function(){n.qp?n.qp.apply(n,arguments):n.queue.push(arguments);};
n.queue=[];t=document.createElement(e);t.async=!0;t.src=v;s=document.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s);}(window, 'script', 'https://a.quora.com/qevents.js');
qp('init','${quoraId}');
qp('track','ViewContent');`
    );
  }

  const bingId = (seo.bing_pixel_id || "").trim();
  if (bingId && /^\d{5,20}$/.test(bingId)) {
    injectInlineScript(
      "bing-base",
      `(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"${bingId}"};
o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,
n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},
i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","https://bat.bing.com/bat.js","uetq");`
    );
  }

  const gtmId = (seo.google_tm_pixel_id || "").trim();
  if (gtmId && /^GTM-[A-Z0-9]{4,12}$/i.test(gtmId)) {
    injectInlineScript(
      "gtm-base",
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`
    );
  }

  const adsId = (seo.google_adwords_pixel_id || "").trim();
  const gaId = (seo.google_analytics_id || "").trim();
  const awId = adsId
    ? /^AW-/i.test(adsId)
      ? adsId
      : `AW-${adsId}`
    : "";

  if (
    (gaId && /^(G-[A-Z0-9]{6,12}|UA-\d{4,12}-\d{1,4})$/i.test(gaId)) ||
    (awId && /^AW-\d{6,20}$/i.test(awId))
  ) {
    const primaryId = gaId || awId;
    injectExternalScript(
      "gtag-js",
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`
    );
    injectInlineScript(
      "gtag-config",
      `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
${gaId ? `gtag('config', '${gaId}');` : ""}
${awId ? `gtag('config', '${awId}');` : ""}`
    );
  }
};

/**
 * Apply SEO meta for a company listing page (no remarketing pixels).
 * Pixels are booking-page only per Marketing Tools requirements.
 */
export const applyCompanyMarketingTools = (seo) => {
  const meta = buildCompanySeoMeta(seo);
  if (meta) {
    applySeoMeta(meta);
  }
};

/**
 * Apply SEO meta (with event-name inheritance) and pixels for a booking page.
 */
export const applyEventMarketingTools = (seo, eventName) => {
  const meta = buildEventSeoMeta(seo, eventName);
  if (meta) {
    applySeoMeta(meta);
  }
  injectMarketingPixels(seo);
};
